import { getAuth } from "firebase/auth"
import { collection, doc, DocumentData, DocumentReference, DocumentSnapshot, getDoc, getDocFromServer, getDocs, getFirestore, orderBy, query, setDoc, limit, getCountFromServer, startAfter, arrayUnion, deleteDoc, arrayRemove, where, Timestamp } from "firebase/firestore"
import { app, functions } from "./firebase"
import { httpsCallable } from "firebase/functions"
import { getUserId, userIdExists } from "utils/firebaseHelpers"
import { getDownloadURL, ref, getStorage } from "firebase/storage"
import { AccessInvitation, AccessInvitationRead, AccessRole, AccountPrivateInfoWrite, Category, CategoryWrite, Location, LocationWrite, Customer, CustomerWrite, Supplier, SupplierWrite, CustomInputGroups, ProductAction, ProductWrite, ProductRead, ProductImage, ProductAiSuggestion, SortDirection, Storage, StorageWrite, StorageRead, TransactionWrite, TransactionType, Notification, CustomInputWrite, CustomerRead, SupplierRead, AccountPublicWrite, AccountPublicRead, AccountPrivateInfoRead, ProductActionWrite, ProductActionRead, BarcodeRead } from "types"
import { 
    /* Lines 8-1797 omitted */
    TransactionRead,
} from '@/types';
import { UserDocNotFoundError, UserNonExistingIdError } from "utils/CustomError"
import { logger } from "../utils/secureLogger"
import { TransactionsSortBy } from "@/hooks/useTransactions"
import { ProductsSortBy } from "@/hooks/useProducts"
import { ActionsSortBy } from "@/hooks/useActions"
import { AccessInvitationsSortBy } from "@/hooks/useAccess"
import { CategoriesSortBy } from "@/hooks/useCategories"
import { LocationsSortBy } from "@/hooks/useLocations"

const auth = getAuth(app)
const firestore = getFirestore(app)

/**
 * Load a user document from a user document reference.
 * Returns an Account object with name, avatarColor, and optionally avatarUrl.
 */
export const loadUserFromRef = async (userRef: DocumentReference): Promise<AccountPublicRead> => {
    try {
        // Use the getUserPublicDoc function to load public user data
        return await getUserPublicDoc(userRef.id);
    } catch (error) {
        logger.error('Failed to load user from ref', { error: error instanceof Error ? error.message : String(error) });
        // Return placeholder on error
        return {
            _version: 1,
            name: '',
            other: {
                avatarColor: '#3B82F6',
                hasAvatar: false,
            },
            id: userRef.id,
        } as AccountPublicRead;
    }
};

/**
 * Load multiple user documents from user document references in parallel.
 */
export const loadUsersFromRefs = async (userRefs: DocumentReference[]): Promise<AccountPublicRead[]> => {
    return Promise.all(userRefs.map(ref => loadUserFromRef(ref)));
};

export const getNotifications = async (uid: string): Promise<Notification[]> => {
    try {
        const firestore = getFirestore(app);
        const notificationsRef = collection(firestore, 'users', uid, 'private', 'information', 'notifications');
        const q = query(notificationsRef, orderBy('createdAt', 'desc'));
        const snap = await getDocs(q);
        let notifications: Notification[] = [];
        snap.forEach((doc) => {
            notifications.push({
                id: doc.id,
                ...doc.data()
            } as Notification);
        });
        return notifications;
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        logger.error('Failed to get notifications', { error: errorMessage, uid });
        throw error instanceof Error ? error : new Error(errorMessage);
    }
};

/**
 * Mark notification as read.
 */
export const markNotificationAsRead = async (notificationId: string) => {
    // Check if the user is logged in.
    if (!userIdExists(auth)) throw new UserNonExistingIdError()
    try {
        // Calls the backend callable function
        const fn = httpsCallable(functions, "markNotificationAsRead");
        await fn({ notificationId });
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        logger.error('Failed to mark notification as read', { error: errorMessage, notificationId });
        throw error instanceof Error ? error : new Error(errorMessage);
    }
};

/**
 * Mark notification as unread.
 */
export const markNotificationAsUnread = async (notificationId: string) => {
    console.log('markNotificationAsUnread called with', { notificationId });
    // Check if the user is logged in.
    if (!userIdExists(auth)) throw new UserNonExistingIdError()
    try {
        // Calls the backend callable function
        const fn = httpsCallable(functions, "markNotificationAsUnread");
        await fn({ notificationId });
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        logger.error('Failed to mark notification as unread', { error: errorMessage, notificationId });
        throw error instanceof Error ? error : new Error(errorMessage);
    }
};

/**
 * Delete notification.
 */
export const deleteNotification = async (notificationId: string) => {
    // Check if the user is logged in.
    if (!userIdExists(auth)) throw new UserNonExistingIdError()
    try {
        // Calls the backend callable function
        const fn = httpsCallable(functions, "deleteNotification");
        await fn({ notificationId });
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        logger.error('Failed to delete notification', { error: errorMessage, notificationId });
        throw error instanceof Error ? error : new Error(errorMessage);
    }
};

/**
 * Get user public doc snap.
 */
export const getUserDocSnap = async (uid: string): Promise<DocumentSnapshot<DocumentData>> => {
    logger.api(`getUserDocSnap`, { uid }, 'FIREBASE_API');
    // Get Firestore instance.
    const firestore = getFirestore(app)
    // Get the user document.
    const userDocRef = doc(firestore, 'users', uid)
    // Get the user document snapshot.
    return await getDoc(userDocRef)
}

/**
 * Get user public document data.
 */
export const getUserPublicDoc = async (uid?: string): Promise<AccountPublicRead> => {

    // If uid is not provided, get from auth
    if (!uid) {
        if (!userIdExists(auth)) throw new UserNonExistingIdError();
        uid = getUserId(auth);
    }

    logger.api(`getUserPublicDoc`, { uid }, 'FIREBASE_API');
    
    const userDocSnap = await getUserDocSnap(uid);
    const publicData = userDocSnap.data();
    
    if (!publicData) {
        throw new UserDocNotFoundError();
    }
    
    // Get user avatar url from storage if exists
    const storage = getStorage(app);
    const avatarRef = ref(storage, `users/${uid}/avatar.jpg`);
    
    let avatarUrl: string | undefined;
    if (publicData?.other?.hasAvatar === true) {
        try {
            avatarUrl = await getDownloadURL(avatarRef);
        } catch (error: unknown) {
            logger.warn(`Failed to get avatar url`, error, 'FIREBASE_API');
        }
    }
    
    return {
        _version: publicData._version || 1,
        name: publicData.name || '',
        id: uid,
        isDeleted: publicData.isDeleted || false,
        keyHistory: {
            createdAt: publicData.keyHistory?.createdAt || null,
            modifiedAt: publicData.keyHistory?.modifiedAt || null,
            deletedAt: publicData.keyHistory?.deletedAt || null,
        },
        other: {
            avatarColor: publicData.other?.avatarColor || '#3B82F6',
            hasAvatar: publicData.other?.hasAvatar || false,
            isComplete: publicData.other?.isComplete || false,
            avatarUrl: avatarUrl,
        },
    };
}

/**
 * Get user private document data.
 */
export const getUserPrivateDoc = async (): Promise<AccountPrivateInfoRead> => {

    // If uid is not provided, get from auth
    if (!userIdExists(auth)) throw new UserNonExistingIdError();
    const uid = getUserId(auth);

    logger.api(`getUserPrivateDoc`, { uid }, 'FIREBASE_API');
    
    const privateDocRef = doc(firestore, 'users', uid, 'private', 'information');
    const privateDocSnap = await getDoc(privateDocRef);
    const privateData = privateDocSnap.data();
    
    return {
        isBusiness: privateData?.isBusiness || false,
        org: {
            orgName: privateData?.org?.orgName || '',
            orgNum: privateData?.org?.orgNum || '',
            vatNum: privateData?.org?.vatNum || '',
        },
        postalAddress: {
            address: privateData?.postalAddress?.address || '',
            city: privateData?.postalAddress?.city || '',
            state: privateData?.postalAddress?.state || '',
            zip: privateData?.postalAddress?.zip || '',
            country: privateData?.postalAddress?.country || '',
            co: privateData?.postalAddress?.co || '',
        },
        contact: {
            contactPerson: privateData?.contact?.contactPerson || '',
            phone: privateData?.contact?.phone || '',
        },
        counters: {
            notificationCounter: privateData?.counters?.notificationCounter || 0,
        },
        accessInvitations: privateData?.accessInvitations || [],
        storageIds: privateData?.storageIds || [],
    };
}

/**
 * Get users access invitations.
 * 
 * To get the access invitations we make use of the access invitation doc
 * references stored in the user, which we reload here to get the latest.
 * Note that this might be redundant, but only requires one additional read.
 * 
 * @param storageId - Optional storage ID to include in the returned data
 */
export const getUserAccessInvitationDocs = async (storageId?: string) => {
    logger.api(`getUserAccessInvitationDocs`, { storageId: storageId ? logger.sanitizeData(storageId) : undefined }, `FIREBASE_API`)
    // Check if user is logged in
    if (!userIdExists(auth)) throw new UserNonExistingIdError()
    // Get user id
    const uid = getUserId(auth)
    const userDocSnap = await getUserDocSnap(uid)
    const userDocData = userDocSnap.data()
    if (!userDocData) throw new UserNonExistingIdError()

    const privateDoc = await getUserPrivateDoc()
    const accessInvitationPaths = [...new Set([
        ...(privateDoc.accessInvitations || []),
        ...(Array.isArray(userDocData.accessInvitations) ? userDocData.accessInvitations : []),
    ])]

    const accessInvitationDocs: AccessInvitation[] = []
    await Promise.all(
        accessInvitationPaths.map(async (path: string) => {
            try {
                const docRef = doc(firestore, path)
                const docSnap = await getDoc(docRef)
                const docData = docSnap.data() as AccessInvitation
                if (docData) {
                    const user = docData.userId ? await getUserPublicDoc(docData.userId) : {
                        _version: 1 as const,
                        name: 'Unknown User',
                        other: {
                            avatarColor: '#3B82F6',
                            isComplete: false,
                        },
                        id: 'unknown',
                    };

                    const pathParts = path.split('/').filter(Boolean)
                    const storageIdFromPath = pathParts[0] === 'storages' ? pathParts[1] : undefined
                    const resolvedStorageId = docData.storage?.storageId || storageIdFromPath
                    let storageName = docData.storage?.storageName || ''

                    if (!storageName && resolvedStorageId) {
                        try {
                            const storageSnap = await getDoc(doc(firestore, 'storages', resolvedStorageId))
                            const storageData = storageSnap.data()
                            if (storageData?.name) {
                                storageName = storageData.name
                            }
                        } catch {
                            // Invitees may not be able to read the storage until they accept.
                        }
                    }

                    accessInvitationDocs.push({
                        ...docData,
                        id: docRef.id,
                        storage: {
                            storageId: resolvedStorageId,
                            storageName,
                        },
                        user,
                    } as AccessInvitation)
                }
            } catch (error: unknown) {
                const errorMessage = error instanceof Error ? error.message : String(error)
                logger.warn('Failed to load access invitation', { error: errorMessage, path }, 'FIREBASE_API')
            }
        })
    )
    // Finally, we return the results
    return accessInvitationDocs
}

/**
 * Reply to access invitation.
 * 
 * The user can eigher accept or reject the access invitation,
 * based on a boolean value being either true or false.
 */
export const respondToAccessInvitation = async ({ storageId, accessId, accept }: { storageId: string, accessId: string, accept: boolean }) => {
    logger.api(`respondToAccessInvitation`, { storageId, accessId, accept }, 'FIREBASE_API');
    // Check if user is logged in
    if (!userIdExists(auth)) throw new UserNonExistingIdError()
    
    try {
        const respondToInvitation = httpsCallable(functions, 'respondToAccessInvitation')
        await respondToInvitation({ storageId, accessId, accept })
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        logger.error('Failed to respond to access invitation', { error: errorMessage, storageId, accessId })
        throw error instanceof Error ? error : new Error(errorMessage)
    }
}

/**
 * Delete access invitation.
 * 
 * Allows the user to delete an access invitation document.
 * This also removed the reference from the user's document (field storages),
 * if it's present.
 */
export const deleteAccessInvitationDoc = async ({ storageId, accessId }: { storageId: string, accessId: string }) => {
    logger.api(`deleteAccessInvitationDoc`, { storageId, accessId }, 'FIREBASE_API');
    // Check if user is logged in
    if (!userIdExists(auth)) throw new UserNonExistingIdError()
    
    try {
        const deleteAccessInvitation = httpsCallable(functions, 'deleteAccessInvitation')
        await deleteAccessInvitation({ storageId, accessId })
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        logger.error('Failed to delete access invitation', { error: errorMessage, storageId, accessId })
        throw error instanceof Error ? error : new Error(errorMessage)
    }
}

/**
 * Get account data.
 */
/*export const getUserDoc = async (): Promise<AccountRead> => {
    logger.api(`getUserDoc`, undefined, 'FIREBASE_API');
    // Check if the user is logged in.
    if (!userIdExists(auth)) throw new UserNonExistingIdError()
    // Get user id.
    const uid = getUserId(auth)
    
    // Load public and private data using dedicated functions
    const publicData = await getUserPublicDoc(uid);
    const privateData = await getUserPrivateDoc(uid);
    
    // Merge public and private data following AccountRead type
    return {
        ...publicData,
        ...privateData,
        email: auth.currentUser?.email || undefined,
        other: {
            ...publicData.other,
            isComplete: privateData.isComplete || false,
        },
    };
}*/

/**
 * Set user document.
 */
export const setUserDoc = async (publicData: AccountPublicWrite, privateData: AccountPrivateInfoWrite) => {
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError();
    // Get user id.
    const uid = getUserId(auth);

    logger.api('Setting user document', {
        name: logger.sanitizeData(publicData.name),
        isBusiness: privateData.isBusiness,
        userId: logger.sanitizeData(uid)
    });

    // Prepare file data for server-side processing if file exists
    let fileData = null;
    if (publicData.file) {
        logger.api('File upload detected', {
            fileName: logger.sanitizeData(publicData.file.name),
            fileType: publicData.file.type
        });
        // Validate file type client-side
        const validImageTypes = ['image/jpeg', 'image/png', 'image/webp'];
        if (!validImageTypes.includes(publicData.file.type)) {
            throw new Error('INVALID_FILE_TYPE');
        }
        // Convert file using the existing utility function
        const convertedFiles = await convertFilesToFirebaseFormat([publicData.file]);
        fileData = convertedFiles[0];
    }

    try {
        const updateUser = httpsCallable(functions, 'updateUser');
        // Send public and private data separately to the backend
        const payload = {
            publicData: {
                name: publicData.name,
                other: publicData.other,
            },
            privateData: {
                isBusiness: privateData.isBusiness,
                org: privateData.org,
                postalAddress: privateData.postalAddress,
                contact: privateData.contact,
                file: fileData
            }
        };
        const result = await updateUser(payload);
        
        // The backend returns the updated data, but we need to fetch fresh to get the complete AccountRead
        const publicDocData = await getUserPublicDoc();
        const privateDocData = await getUserPrivateDoc();
        return {
            publicDocData: publicDocData,
            privateDocData: privateDocData
        };
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        logger.error('Failed to set user document', { error: errorMessage, userId: uid });
        throw error instanceof Error ? error : new Error(errorMessage);
    }
}

/**
 * Delete user avatar.
 */
export const deleteUserAvatar = async () => {
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError();
    // Get user id.
    const uid = getUserId(auth);

    logger.api('Deleting user avatar', { userId: logger.sanitizeData(uid) });

    try {
        const deleteAvatar = httpsCallable(functions, 'deleteAvatar');
        const result = await deleteAvatar();
        // Return the user data from server response, which should match AccountPublicRead
        const userData = result.data as AccountPublicRead;
        return userData;
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        logger.error('Failed to delete user avatar', { error: errorMessage, userId: uid });
        throw error instanceof Error ? error : new Error(errorMessage);
    }
}

/**
 * Permanently delete the signed-in account.
 */
export const deleteAccountDoc = async ({ email }: { email: string }) => {
    if (userIdExists(auth) === false) throw new UserNonExistingIdError();
    const uid = getUserId(auth);

    logger.api('Deleting user account', { userId: logger.sanitizeData(uid) });

    try {
        const deleteAccount = httpsCallable(functions, 'deleteAccount');
        await deleteAccount({ email });
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        logger.error('Failed to delete user account', { error: errorMessage, userId: uid });
        throw error instanceof Error ? error : new Error(errorMessage);
    }
}

/**
 * Set user avatar.
 */
export const setUserAvatar = async (accountPublicDocData: AccountPublicWrite) => {
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError();
    // Get user id.
    const uid = getUserId(auth);

    logger.api('Setting user avatar', {
        hasFile: !!accountPublicDocData.file,
        avatarColor: logger.sanitizeData(accountPublicDocData.other.avatarColor),
        userId: logger.sanitizeData(uid)
    });

    // Prepare file data for server-side processing if file exists
    let fileData = null;
    if (accountPublicDocData.file) {
        logger.api('Avatar file upload detected', {
            fileName: logger.sanitizeData(accountPublicDocData.file.name),
            fileType: accountPublicDocData.file.type
        });
        // Validate file type client-side
        const validImageTypes = ['image/jpeg', 'image/png', 'image/webp'];
        if (!validImageTypes.includes(accountPublicDocData.file.type)) {
            throw new Error('INVALID_FILE_TYPE');
        }
        // Convert file using the existing utility function
        const convertedFiles = await convertFilesToFirebaseFormat([accountPublicDocData.file]);
        fileData = convertedFiles[0];
    }

    try {
        const updateAvatar = httpsCallable(functions, 'updateUserAvatar');
        const payload = {
            file: fileData,
            other: {
                avatarColor: accountPublicDocData.other.avatarColor,
            }
        };
        const result = await updateAvatar(payload);
        // Return the user data from server response, which should match AccountRead
        const userData = result.data as AccountPublicRead;
        // Get avatar URL if user has avatar
        if (userData.other?.hasAvatar) {
            const storage = getStorage(app);
            const avatarRef = ref(storage, `users/${uid}/avatar.jpg`);
            try {
                const avatarUrl = await getDownloadURL(avatarRef);
                userData.other.avatarUrl = avatarUrl;
            } catch (error: unknown) {
                logger.warn('Failed to get avatar URL after update', { error: error instanceof Error ? error.message : String(error), userId: uid });
            }
        }
        return userData;
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        logger.error('Failed to set user avatar', { error: errorMessage, userId: uid });
        throw error instanceof Error ? error : new Error(errorMessage);
    }
}

/**
 * Delete category.
 */
export const deleteCategoryDoc = async ({ storageId, categoryId }: { storageId: string, categoryId: string }) => {
    logger.api('Deleting category document', { 
        storageId: logger.sanitizeData(storageId), 
        categoryId: logger.sanitizeData(categoryId) 
    })
    // Check if the user is logged in.
    if (!userIdExists(auth)) throw new UserNonExistingIdError()
    
    try {
        const deleteCategory = httpsCallable(functions, 'deleteCategory')
        await deleteCategory({ storageId, categoryId })
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        logger.error('Failed to delete category document', { error: errorMessage, storageId, categoryId })
        throw error instanceof Error ? error : new Error(errorMessage)
    }
}

/**
 * Restore category.
 */
export const restoreCategoryDoc = async ({ storageId, categoryId }: { storageId: string, categoryId: string }) => {
    logger.api('Restoring category document', { 
        storageId: logger.sanitizeData(storageId), 
        categoryId: logger.sanitizeData(categoryId) 
    })
    // Check if the user is logged in.
    if (!userIdExists(auth)) throw new UserNonExistingIdError()
    
    try {
        const restoreCategory = httpsCallable(functions, 'restoreCategory')
        await restoreCategory({ storageId, categoryId })
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        logger.error('Failed to restore category document', { error: errorMessage, storageId, categoryId })
        throw error instanceof Error ? error : new Error(errorMessage)
    }
}

/**
 * Set category.
 */
export const setCategoryDoc = async (categoryData: CategoryWrite) => {
    logger.api('Setting category document', { 
        storageId: logger.sanitizeData(categoryData.ids.storageId), 
        categoryId: logger.sanitizeData(categoryData.categoryId),
        name: logger.sanitizeData(categoryData.name)
    })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    
    try {
        const updateCategory = httpsCallable(functions, 'updateCategory')
        const result = await updateCategory(categoryData)
        return result.data
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        logger.error('Failed to set category document', { error: errorMessage, storageId: categoryData.ids.storageId, categoryId: categoryData.ids.categoryId })
        throw error instanceof Error ? error : new Error(errorMessage)
    }
}

/**
 * Add category.
 */
export const addCategoryDoc = async (categoryData: CategoryWrite) => {
    logger.api('Adding category document', { 
        storageId: logger.sanitizeData(categoryData.ids.storageId), 
        id: logger.sanitizeData(categoryData.categoryId), 
        name: logger.sanitizeData(categoryData.name) 
    })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    
    try {
        const createCategory = httpsCallable(functions, 'createCategory')
        console.log("categoryData: ", categoryData);
        const result = await createCategory(categoryData)
        return result.data
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        logger.error('Failed to add category document', { error: errorMessage, storageId: categoryData.ids.storageId })
        throw error instanceof Error ? error : new Error(errorMessage)
    }
}

/**
 * Get categories.
 */
export const getCategoryDocs = async ({
    storageId,
    sortBy = CategoriesSortBy.NAME,
    sortDirection = SortDirection.ASCENDING,
    includeDeleted = false
}: {
    storageId: string,
    sortBy?: CategoriesSortBy,
    sortDirection?: SortDirection,
    includeDeleted?: boolean
}) => {
    logger.api('Getting category documents', { 
        storageId: logger.sanitizeData(storageId),
        sortBy,
        sortDirection,
        includeDeleted
    })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    // Get storage doc ref.
    const storageDocRef = doc(collection(firestore, 'storages'), storageId)
    // Target the collection "categories" ref inside the storage document.
    const storageCategoriesCollRef = collection(storageDocRef, 'categories')
    // Create new categories array.
    const categories: Category[] = []
    
    // Build query with optional isDeleted filter
    const categoriesQuery = includeDeleted
        ? query(storageCategoriesCollRef, orderBy(sortBy, sortDirection))
        : query(storageCategoriesCollRef, where('isDeleted', '==', false), orderBy(sortBy, sortDirection))
        
    // Get all categories.
    const querySnapshot = await getDocs(categoriesQuery)
    await Promise.all(querySnapshot.docs.map(async (doc) => {
        const docData = doc.data()
        categories.push({
            id: doc.id,
            ...docData
        } as Category)
    }))
    return categories
}

/**
 * Get single category.
 */
export const getCategoryDoc = async (storageId: string, categoryId: string) => {
    logger.api('Getting category document', { 
        storageId: logger.sanitizeData(storageId), 
        categoryId: logger.sanitizeData(categoryId) 
    })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    // Get storage doc ref.
    const storageDocRef = doc(collection(firestore, 'storages'), storageId)
    // Target the collection "categories" ref inside the storage document.
    const storageCategoriesCollRef = collection(storageDocRef, 'categories')
    // Get category doc ref.
    const categoryDocRef = doc(storageCategoriesCollRef, categoryId)
    // Get category doc snapshot.
    const categoryDocSnap = await getDoc(categoryDocRef)
    try {
        // Get category data.
        const categoryData = categoryDocSnap.data() as DocumentData
        // Check if the category exists.
        if (!categoryData) throw new Error('Category not found.')
        // Return category data.
        return {
            id: categoryId,
            ...categoryData
        } as Category
    } catch (error: unknown) {
        throw error instanceof Error ? error : new Error(String(error))
    }
}

/**
 * Delete location.
 */
export const deleteLocationDoc = async ({ storageId, locationId }: { storageId: string, locationId: string }) => {
    logger.api('Deleting location document', { 
        storageId: logger.sanitizeData(storageId), 
        locationId: logger.sanitizeData(locationId) 
    })
    if (!userIdExists(auth)) throw new UserNonExistingIdError()
    
    try {
        const deleteLocation = httpsCallable(functions, 'deleteLocation')
        await deleteLocation({ storageId, locationId })
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        logger.error('Failed to delete location document', { error: errorMessage, storageId, locationId })
        throw error instanceof Error ? error : new Error(errorMessage)
    }
}

/**
 * Restore location.
 */
export const restoreLocationDoc = async ({ storageId, locationId }: { storageId: string, locationId: string }) => {
    logger.api('Restoring location document', { 
        storageId: logger.sanitizeData(storageId), 
        locationId: logger.sanitizeData(locationId) 
    })
    if (!userIdExists(auth)) throw new UserNonExistingIdError()
    
    try {
        const restoreLocation = httpsCallable(functions, 'restoreLocation')
        await restoreLocation({ storageId, locationId })
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        logger.error('Failed to restore location document', { error: errorMessage, storageId, locationId })
        throw error instanceof Error ? error : new Error(errorMessage)
    }
}

/**
 * Set location.
 */
export const setLocationDoc = async (locationData: LocationWrite) => {
    logger.api('Setting location document', { 
        storageId: logger.sanitizeData(locationData.ids.storageId), 
        locationId: logger.sanitizeData(locationData.ids.locationId),
        name: logger.sanitizeData(locationData.name)
    })
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    
    try {
        const updateLocation = httpsCallable(functions, 'updateLocation')
        const result = await updateLocation(locationData)
        return result.data
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        logger.error('Failed to set location document', { error: errorMessage, storageId: locationData.ids.storageId, locationId: locationData.ids.locationId })
        throw error instanceof Error ? error : new Error(errorMessage)
    }
}

/**
 * Add location.
 */
export const addLocationDoc = async (locationData: LocationWrite) => {
    logger.api('Adding location document', { 
        storageId: logger.sanitizeData(locationData.ids.storageId), 
        name: logger.sanitizeData(locationData.name)
    })
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    
    try {
        const createLocation = httpsCallable(functions, 'createLocation')
        const result = await createLocation(locationData)
        return result.data
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        logger.error('Failed to add location document', { error: errorMessage, storageId: locationData.ids.storageId })
        throw error instanceof Error ? error : new Error(errorMessage)
    }
}

/**
 * Get locations.
 */
export const getLocationDocs = async ({
    storageId,
    sortBy = LocationsSortBy.NAME,
    sortDirection = SortDirection.ASCENDING,
    includeDeleted = false
}: {
    storageId: string,
    sortBy?: LocationsSortBy,
    sortDirection?: SortDirection,
    includeDeleted?: boolean
}) => {
    logger.api('Getting location documents', { 
        storageId: logger.sanitizeData(storageId),
        sortBy,
        sortDirection,
        includeDeleted
    })
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    const storageDocRef = doc(collection(firestore, 'storages'), storageId)
    const storageLocationsCollRef = collection(storageDocRef, 'locations')
    const querySnapshot = await getDocs(storageLocationsCollRef)
    const locations: Location[] = querySnapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data()
    } as Location))

    const visibleLocations = includeDeleted
        ? locations
        : locations.filter((item) => !item.isDeleted)

    const direction = sortDirection === SortDirection.DESCENDING ? -1 : 1
    visibleLocations.sort((a, b) => {
        const left = (a.name || '').toLowerCase()
        const right = (b.name || '').toLowerCase()
        return left.localeCompare(right) * direction
    })

    return visibleLocations
}

/**
 * Get single location.
 */
export const getLocationDoc = async (storageId: string, locationId: string) => {
    logger.api('Getting location document', { 
        storageId: logger.sanitizeData(storageId), 
        locationId: logger.sanitizeData(locationId) 
    })
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    const storageDocRef = doc(collection(firestore, 'storages'), storageId)
    const storageLocationsCollRef = collection(storageDocRef, 'locations')
    const locationDocRef = doc(storageLocationsCollRef, locationId)
    const locationDocSnap = await getDoc(locationDocRef)
    try {
        const locationData = locationDocSnap.data() as DocumentData
        if (!locationData) throw new Error('Location not found.')
        return {
            id: locationId,
            ...locationData
        } as Location
    } catch (error: unknown) {
        throw error instanceof Error ? error : new Error(String(error))
    }
}

/**
 * Delete customer.
 */
export const deleteCustomerDoc = async ({ storageId, customerId }: { storageId: string, customerId: string }) => {
    logger.api('Deleting customer document', { 
        storageId: logger.sanitizeData(storageId), 
        customerId: logger.sanitizeData(customerId) 
    })
    // Check if the user is logged in.
    if (!userIdExists(auth)) throw new UserNonExistingIdError()
    
    try {
        const deleteCustomer = httpsCallable(functions, 'deleteCustomer')
        await deleteCustomer({ storageId, customerId })
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        logger.error('Failed to delete customer document', { error: errorMessage, storageId, customerId })
        throw error instanceof Error ? error : new Error(errorMessage)
    }
}

/**
 * Restore customer.
 */
export const restoreCustomerDoc = async ({ storageId, customerId }: { storageId: string, customerId: string }) => {
    logger.api('Restoring customer document', { 
        storageId: logger.sanitizeData(storageId), 
        customerId: logger.sanitizeData(customerId) 
    })
    // Check if the user is logged in.
    if (!userIdExists(auth)) throw new UserNonExistingIdError()
    
    try {
        const restoreCustomer = httpsCallable(functions, 'restoreCustomer')
        await restoreCustomer({ storageId, customerId })
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        logger.error('Failed to restore customer document', { error: errorMessage, storageId, customerId })
        throw error instanceof Error ? error : new Error(errorMessage)
    }
}

/**
 * Set customer.
 */
export const setCustomerDoc = async (customerData: CustomerWrite) => {
    logger.api('Setting customer document', { 
        storageId: logger.sanitizeData(customerData.ids.storageId), 
        customerId: logger.sanitizeData(customerData.customerId),
        name: logger.sanitizeData(customerData.name)
    })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    
    try {
        const updateCustomer = httpsCallable(functions, 'updateCustomer')
        const result = await updateCustomer(customerData)
        return result.data
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        logger.error('Failed to set customer document', { error: errorMessage, storageId: customerData.ids.storageId, customerId: customerData.ids.customerId })
        throw error instanceof Error ? error : new Error(errorMessage)
    }
}

/**
 * Add customer.
 */
export const addCustomerDoc = async (customerData: CustomerWrite) => {
    logger.api('Adding customer document', {
        storageId: logger.sanitizeData(customerData.ids.storageId),
        customerId: logger.sanitizeData(customerData.customerId),
        name: logger.sanitizeData(customerData.name)
    })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    
    try {
        const createCustomer = httpsCallable(functions, 'createCustomer')
        const result = await createCustomer(customerData)
        return result.data
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        logger.error('Failed to add customer document', { error: errorMessage, storageId: customerData.ids.storageId })
        throw error instanceof Error ? error : new Error(errorMessage)
    }
}

/**
 * Get customers.
 */
export const getCustomerDocs = async (storageId: string, includeDeleted: boolean = false) => {
    logger.api('Getting customer documents', { storageId: logger.sanitizeData(storageId), includeDeleted })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    // Get storage doc ref.
    const storageDocRef = doc(collection(firestore, 'storages'), storageId)
    // Target the collection "customers" ref inside the storage document.
    const storageCustomersCollRef = collection(storageDocRef, 'customers')
    // Create new customers array.
    const customers: Customer[] = []
    
    // Build query with optional isDeleted filter
    const customersQuery = includeDeleted 
        ? storageCustomersCollRef 
        : query(storageCustomersCollRef, where('isDeleted', '==', false))
    
    // Get all customers.
    await Promise.all((await getDocs(customersQuery)).docs.map(async (doc) => {
        const docData = doc.data()
        customers.push({
            id: doc.id,
            ...docData
        } as CustomerRead)
    }))
    return customers
}

/**
 * Get single customer.
 */
export const getCustomerDoc = async (storageId: string, customerId: string) => {
    logger.api('Getting customer document', { 
        storageId: logger.sanitizeData(storageId), 
        customerId: logger.sanitizeData(customerId) 
    })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    // Get storage doc ref.
    const storageDocRef = doc(collection(firestore, 'storages'), storageId)
    // Target the collection "customers" ref inside the storage document.
    const storageCustomersCollRef = collection(storageDocRef, 'customers')
    // Get customer doc ref.
    const customerDocRef = doc(storageCustomersCollRef, customerId)
    // Get customer doc snapshot.
    const customerDocSnap = await getDoc(customerDocRef)
    try {
        // Get customer data.
        const customerData = customerDocSnap.data() as DocumentData
        // Check if the customer exists.
        if (!customerData) throw new Error('Customer not found.')
        // Return customer data.
        return {
            id: customerId,
            ...customerData
        } as Customer
    } catch (error: unknown) {
        throw error instanceof Error ? error : new Error(String(error))
    }
}

// ============================= SUPPLIER CRUD OPERATIONS =============================

/**
 * Add supplier.
 */
export const addSupplierDoc = async (supplierData: SupplierWrite) => {
    logger.api('Adding supplier document', {
        storageId: logger.sanitizeData(supplierData.ids.storageId),
        supplierId: logger.sanitizeData(supplierData.supplierId),
        name: logger.sanitizeData(supplierData.name)
    })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    
    try {
        const createSupplier = httpsCallable(functions, 'createSupplier')
        const result = await createSupplier(supplierData)
        return result.data
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        logger.error('Failed to add supplier document', { error: errorMessage, storageId: supplierData.ids.storageId })
        throw error instanceof Error ? error : new Error(errorMessage)
    }
}

/**
 * Get suppliers.
 */
export const getSupplierDocs = async (storageId: string, includeDeleted: boolean = false) => {
    logger.api('Getting supplier documents', { storageId: logger.sanitizeData(storageId), includeDeleted })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    // Get storage doc ref.
    const storageDocRef = doc(collection(firestore, 'storages'), storageId)
    // Target the collection "suppliers" ref inside the storage document.
    const storageSuppliersCollRef = collection(storageDocRef, 'suppliers')
    // Create new suppliers array.
    const suppliers: Supplier[] = []
    
    // Build query with optional isDeleted filter
    const suppliersQuery = includeDeleted 
        ? storageSuppliersCollRef 
        : query(storageSuppliersCollRef, where('isDeleted', '==', false))
    
    // Get all suppliers.
    await Promise.all((await getDocs(suppliersQuery)).docs.map(async (doc) => {
        const docData = doc.data()
        suppliers.push({
            id: doc.id,
            ...docData
        } as SupplierRead)
    }))
    return suppliers
}

/**
 * Get single supplier.
 */
export const getSupplierDoc = async (storageId: string, supplierId: string) => {
    logger.api('Getting supplier document', { 
        storageId: logger.sanitizeData(storageId), 
        supplierId: logger.sanitizeData(supplierId) 
    })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    // Get storage doc ref.
    const storageDocRef = doc(collection(firestore, 'storages'), storageId)
    // Target the collection "suppliers" ref inside the storage document.
    const storageSuppliersCollRef = collection(storageDocRef, 'suppliers')
    // Get supplier doc ref.
    const supplierDocRef = doc(storageSuppliersCollRef, supplierId)
    // Get supplier doc snapshot.
    const supplierDocSnap = await getDoc(supplierDocRef)
    try {
        // Get supplier data.
        const supplierData = supplierDocSnap.data() as DocumentData
        // Check if the supplier exists.
        if (!supplierData) throw new Error('Supplier not found.')
        // Return supplier data.
        return {
            id: supplierId,
            ...supplierData
        } as Supplier
    } catch (error: unknown) {
        throw error instanceof Error ? error : new Error(String(error))
    }
}

/**
 * Update supplier.
 */
export const setSupplierDoc = async (supplierData: SupplierWrite) => {
    logger.api('Updating supplier document', {
        storageId: logger.sanitizeData(supplierData.ids.storageId),
        supplierId: logger.sanitizeData(supplierData.supplierId)
    })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    
    try {
        const updateSupplier = httpsCallable(functions, 'updateSupplier')
        const result = await updateSupplier(supplierData)
        return result.data
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        logger.error('Failed to update supplier document', { error: errorMessage, storageId: supplierData.ids.storageId, supplierId: supplierData.ids.supplierId })
        throw error instanceof Error ? error : new Error(errorMessage)
    }
}

/**
 * Delete supplier.
 */
export const deleteSupplierDoc = async ({ storageId, supplierId }: { storageId: string, supplierId: string }) => {
    logger.api('Deleting supplier document', {
        storageId: logger.sanitizeData(storageId),
        supplierId: logger.sanitizeData(supplierId)
    })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    
    try {
        const deleteSupplier = httpsCallable(functions, 'deleteSupplier')
        const result = await deleteSupplier({
            storageId,
            supplierId
        })
        return result.data
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        logger.error('Failed to delete supplier document', { error: errorMessage, storageId, supplierId })
        throw error instanceof Error ? error : new Error(errorMessage)
    }
}

/**
 * Restore supplier.
 */
export const restoreSupplierDoc = async ({ storageId, supplierId }: { storageId: string, supplierId: string }) => {
    logger.api('Restoring supplier document', {
        storageId: logger.sanitizeData(storageId),
        supplierId: logger.sanitizeData(supplierId)
    })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    
    try {
        const restoreSupplier = httpsCallable(functions, 'restoreSupplier')
        const result = await restoreSupplier({
            storageId,
            supplierId
        })
        return result.data
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        logger.error('Failed to restore supplier document', { error: errorMessage, storageId, supplierId })
        throw error instanceof Error ? error : new Error(errorMessage)
    }
}

/**
 * Load products.
 */
export const getProductDocs = async ({ 
    storageId, 
    docLimit, 
    startAfterData, 
    sortBy, 
    sortDirection,
    includeDeleted = false
}: { 
    storageId: string, 
    docLimit: number, 
    startAfterData: string | number | null | undefined,
    sortBy: ProductsSortBy,
    sortDirection: SortDirection,
    includeDeleted?: boolean
}) => {
    logger.api('Loading product documents', { 
        storageId: logger.sanitizeData(storageId), 
        docLimit, 
        startAfterData: logger.sanitizeData(startAfterData),
        sortBy,
        sortDirection,
        includeDeleted
    })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    // Get storage doc ref.
    const storageDocRef = doc(collection(firestore, 'storages'), storageId)
    // Target the collection "products" ref inside the storage document.
    const storageProductsCollRef = collection(storageDocRef, 'products')
    // Create new products array.
    const products: ProductRead[] = []
    try {
        // Get products with pagination and sorting.
        logger.debug('Getting products with pagination', { startAfterData: logger.sanitizeData(startAfterData) })
        
        // Map sortBy to actual Firestore field paths
        let firestoreSortField: string = sortBy;
        if (sortBy === ProductsSortBy.AMOUNT) {
            firestoreSortField = 'status.currentAmount';
        }
        
        // Build query with optional isDeleted filter
        let productsQuery;
        if (includeDeleted) {
            // Include all products
            productsQuery = startAfterData 
                ? query(storageProductsCollRef, orderBy(firestoreSortField, sortDirection), startAfter(startAfterData), limit(docLimit)) 
                : query(storageProductsCollRef, orderBy(firestoreSortField, sortDirection), limit(docLimit))
        } else {
            // Filter out deleted products
            productsQuery = startAfterData 
                ? query(storageProductsCollRef, where('isDeleted', '==', false), orderBy(firestoreSortField, sortDirection), startAfter(startAfterData), limit(docLimit)) 
                : query(storageProductsCollRef, where('isDeleted', '==', false), orderBy(firestoreSortField, sortDirection), limit(docLimit))
        }

        const querySnapshot = await getDocs(productsQuery)
        const productDocs = await Promise.all(querySnapshot.docs.map(async (doc) => {
            const docData = doc.data() as ProductRead
            const productActionsCollRef = collection(doc.ref, 'actions')
            const productActionsAmount = await getCountFromServer(productActionsCollRef)
            return {
                ...docData,
                id: doc.id,
                actionAmount: productActionsAmount.data().count,
                counters: {
                    actionsCount: productActionsAmount.data().count,
                },
            } as ProductRead
        }))
        products.push(...productDocs)
        // Client-side sorting commented out to prevent pagination issues
        // Firestore query already handles sorting via orderBy()
        // products.sort((a, b) => {
        //     const aValue = a.status?.amount ?? a.sku ?? '';
        //     const bValue = b.status?.amount ?? b.sku ?? '';
        //     if (sortDirection === "asc") {
        //         return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
        //     } else {
        //         return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
        //     }
        // });
    } catch (error: unknown) {
        throw error instanceof Error ? error : new Error(String(error))
    }
    return products
}

/**
 * Load product doc.
 */
export const getProductDoc = async ({ storageId, productId }: { storageId: string, productId: string }) => {
    logger.api('Loading product document', { 
        storageId: logger.sanitizeData(storageId), 
        productId: logger.sanitizeData(productId) 
    })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    // Get storage doc ref.
    try {
        const storageDocRef = doc(collection(firestore, 'storages'), storageId)
        // Target the collection "products" ref inside the storage document.
        const storageProductsCollRef = collection(storageDocRef, 'products')
        // Get product doc ref.
        const productDocRef = doc(storageProductsCollRef, productId)
        // Get product doc snapshot.
        const productDocSnap = await getDoc(productDocRef)
        // Get product data - need to cast to any first to access old structure
        const productData = productDocSnap.data() as ProductRead
        // Get amount of docs from actions collection
        const productActionsCollRef = collection(productDocRef, 'actions')
        const productActionsAmount = await getCountFromServer(productActionsCollRef)
        // Return product data.
        return {
            ...productData,
            id: productId,
            _version: 1,
            actionAmount: productActionsAmount.data().count,
            counters: {
                actionsCount: productActionsAmount.data().count,
            },
        } as ProductRead
    } catch (error: unknown) {
        throw error instanceof Error ? error : new Error(String(error))
    }
}

/**
 * Load a barcode mapping document for a storage.
 * Document id is the normalized barcode (UPC / EAN).
 */
export const getBarcodeDoc = async ({ storageId, barcode }: { storageId: string, barcode: string }): Promise<BarcodeRead | null> => {
    const normalized = barcode.trim()
    logger.api('Loading barcode document', {
        storageId: logger.sanitizeData(storageId),
        barcode: logger.sanitizeData(normalized),
    })
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    if (!storageId || !normalized) return null
    try {
        const barcodeDocRef = doc(firestore, 'storages', storageId, 'barcodes', normalized.replace(/\//g, '_'))
        const barcodeDocSnap = await getDoc(barcodeDocRef)
        if (!barcodeDocSnap.exists()) return null
        const data = barcodeDocSnap.data() as BarcodeRead
        return {
            _version: 1,
            barcode: data.barcode || normalized,
            sku: data.sku,
            productId: data.productId,
        }
    } catch (error: unknown) {
        throw error instanceof Error ? error : new Error(String(error))
    }
}

/**
 * Get product amount history entries with optional time range filtering.
 * Used for displaying historical stock changes in charts.
 */
export const getProductAmountHistory = async ({ 
    storageId, 
    productId, 
    daysBack 
}: { 
    storageId: string
    productId: string
    daysBack?: number 
}) => {
    logger.api('Loading product amount history', { 
        storageId: logger.sanitizeData(storageId), 
        productId: logger.sanitizeData(productId),
        daysBack 
    })
    
    // Check if the user is logged in
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    
    try {
        const storageDocRef = doc(collection(firestore, 'storages'), storageId)
        const productDocRef = doc(collection(storageDocRef, 'products'), productId)
        const historyCollRef = collection(productDocRef, 'amountHistoryDaily')
        
        // Build query with time filtering if specified
        let historyQuery = query(
            historyCollRef,
            orderBy('history.createdAt', 'desc')
        )
        
        // If daysBack is specified, filter by date
        if (daysBack) {
            const cutoffDate = new Date()
            cutoffDate.setDate(cutoffDate.getDate() - daysBack)
            historyQuery = query(
                historyCollRef,
                where('history.createdAt', '>=', Timestamp.fromDate(cutoffDate)),
                orderBy('history.createdAt', 'desc')
            )
        }
        
        const historySnapshot = await getDocs(historyQuery)
        const historyEntries = historySnapshot.docs.map(doc => {
            const data = doc.data();
            return {
                amount: data.amount,
                delta: data.delta,
                actionType: data.actionType,
                ids: data.ids,
                id: doc.id,
                keyHistory: {
                    createdAt: data.history?.createdAt,
                }
            };
        })
        return historyEntries
    } catch (error: unknown) {
        logger.error('Failed to load product amount history', { 
            error: error instanceof Error ? error.message : String(error),
            storageId,
            productId 
        })
        throw error instanceof Error ? error : new Error(String(error))
    }
}

/**
 * Convert File objects to the format expected by Firebase Functions
 */
const convertFilesToFirebaseFormat = async (files: File[]) => {
    const convertedFiles = []
    
    for (const file of files) {
        // Convert file to base64 data URL
        const base64Data = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result as string)
            reader.onerror = () => reject(new Error('Failed to read file'))
            reader.readAsDataURL(file)
        })
        
        convertedFiles.push({
            name: file.name,
            type: file.type,
            size: file.size,
            data: base64Data
        })
    }
    
    return convertedFiles
}

/**
 * Suggest product fields from uploaded images.
 */
export const suggestProductFromImages = async (storageId: string, files: File[]) => {
    logger.api('Suggesting product from images', {
        storageId: logger.sanitizeData(storageId),
        imageCount: files.length,
    })
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    try {
        const suggest = httpsCallable(functions, 'suggestProductFromImages', { timeout: 120000 })
        const result = await suggest({
            storageId,
            images: {
                files: await convertFilesToFirebaseFormat(files),
            },
        })
        return result.data as { suggestion: ProductAiSuggestion; remainingToday: number }
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        logger.error('Failed to suggest product from images', { error: errorMessage, storageId })
        throw error instanceof Error ? error : new Error(errorMessage)
    }
}

/**
 * Add product.
 */
export const addProductDoc = async (productData: ProductWrite) => {
    const storageId = productData.ids?.storageId!;
    const files = productData.images?.files;
    
    logger.api('Adding product document', { 
        storageId: logger.sanitizeData(storageId), 
        sku: logger.sanitizeData(productData.sku), 
        name: logger.sanitizeData(productData.name),
        imageCount: files?.length || 0,
        isDraft: productData.isDraft || false
    })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    
    try {

        let productDataCopy = { ...productData };
        // Convert files to the format expected by Firebase Functions
        productDataCopy.images = {
            files: files && files.length > 0 ? await convertFilesToFirebaseFormat(files as File[]) : []
        }

        const createProduct = httpsCallable(functions, 'createProduct')
        const result = await createProduct(productDataCopy)
        return result.data
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        logger.error('Failed to add product document', { error: errorMessage, storageId })
        throw error instanceof Error ? error : new Error(errorMessage)
    }
}

/**
 * Set product.
 */
export const setProductDoc = async (productData: ProductWrite) => {
    const { storageId, productId } = productData.ids!;
    const files = productData.images?.files;
    const existingImages = productData.images?.existingImages;
    const imagesToDelete = productData.images?.imagesToDelete;
    
    logger.api('Setting product document', { 
        storageId: logger.sanitizeData(storageId), 
        productId: logger.sanitizeData(productId), 
        sku: logger.sanitizeData(productData.sku), 
        name: logger.sanitizeData(productData.name),
        newImageCount: files?.length || 0,
        imagesToDeleteCount: imagesToDelete?.length || 0
    })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    
    try {

        let productDataCopy = { ...productData };
        // Convert files to the format expected by Firebase Functions
        productDataCopy.images = {
            files: files && files.length > 0 ? await convertFilesToFirebaseFormat(files as File[]) : [],
            existingImages: existingImages || [],
            imagesToDelete: imagesToDelete || []
        }

        const updateProduct = httpsCallable(functions, 'updateProduct')
        console.log("productDataCopy: ", productDataCopy);
        const result = await updateProduct(productDataCopy)
        return result.data
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        logger.error('Failed to set product document', { error: errorMessage, storageId, productId })
        throw error instanceof Error ? error : new Error(errorMessage)
    }
}

/**
 * Delete product.
 */
export const deleteProductDoc = async ({ storageId, productId }: { storageId: string, productId: string }) => {
    logger.api('Deleting product document', { 
        storageId: logger.sanitizeData(storageId), 
        productId: logger.sanitizeData(productId) 
    })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    
    try {
        const deleteProduct = httpsCallable(functions, 'deleteProduct')
        await deleteProduct({ storageId, productId })
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        logger.error('Failed to delete product document', { error: errorMessage, storageId, productId })
        throw error instanceof Error ? error : new Error(errorMessage)
    }
}

/**
 * Restore product.
 */
export const restoreProductDoc = async ({ storageId, productId }: { storageId: string, productId: string }) => {
    logger.api('Restoring product document', { 
        storageId: logger.sanitizeData(storageId), 
        productId: logger.sanitizeData(productId) 
    })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    
    try {
        const restoreProduct = httpsCallable(functions, 'restoreProduct')
        await restoreProduct({ storageId, productId })
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        logger.error('Failed to restore product document', { error: errorMessage, storageId, productId })
        throw error instanceof Error ? error : new Error(errorMessage)
    }
}

/**
 * Add storage.
 */
export const addStorageDoc = async (storageData: Storage) => {
    logger.api('Adding storage document', { 
        name: logger.sanitizeData(storageData.name), 
        description: logger.sanitizeData(storageData.description) 
    })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    
    const createStorageFunction = httpsCallable(functions, 'createStorage');
    
    try {
        const result = await createStorageFunction(storageData);
        
        const responseData = result.data as any;
        
        logger.debug('Storage creation successful', { 
            id: logger.sanitizeData(responseData.id),
            name: logger.sanitizeData(name) 
        })
        
        return responseData; // Return the full storage data
    } catch (error) {
        logger.error('Failed to create storage', error)
        throw error;
    }
}

/**
 * Set storage.
 */
export const setStorageDoc = async (storageData: StorageWrite) => {
    logger.api('Setting storage document', storageData)
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    
    const updateStorageFunction = httpsCallable(functions, 'updateStorage');
    
    try {
        console.log("storageData: ", storageData);
        const result = await updateStorageFunction(storageData);
        console.log("storageData");
        const responseData = result.data as any;
        return responseData; // Return the updated storage data
    } catch (error) {
        logger.error('Failed to update storage', error)
        throw error;
    }
}

/**
 * Delete storage.
 */
export const deleteStorageDoc = async ({
    storageId,
    instantDeletion = false,
}: {
    storageId: string
    instantDeletion?: boolean
}) => {
    logger.api('Deleting storage document', { storageId: logger.sanitizeData(storageId), instantDeletion })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    
    const deleteStorageFunction = httpsCallable(functions, 'deleteStorage');
    
    try {
        const result = await deleteStorageFunction({
            storageId,
            instantDeletion,
        });
        
        const responseData = result.data as any;
        
        logger.debug('Storage deletion successful', { 
            storageId: logger.sanitizeData(storageId) 
        })
        
        return responseData; // Return deletion confirmation with storage ID
    } catch (error) {
        logger.error('Failed to delete storage', error)
        throw error;
    }
}

/**
 * Restore storage.
 */
export const restoreStorageDoc = async (storageId: string) => {
    logger.api('Restoring storage document', { storageId: logger.sanitizeData(storageId) })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    
    const restoreStorageFunction = httpsCallable(functions, 'restoreStorage');
    
    try {
        const result = await restoreStorageFunction({
            storageId
        });
        
        const responseData = result.data as any;
        
        logger.debug('Storage restoration successful', { 
            storageId: logger.sanitizeData(storageId) 
        })
        
        return responseData; // Return restoration confirmation with storage ID
    } catch (error) {
        logger.error('Failed to restore storage', error)
        throw error;
    }
}

/**
 * Get storages.
 */
export const getStorageDocs = async (includeDeleted: boolean = false) => {
    logger.api('Getting storage documents', { includeDeleted })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    // Get user id.
    const uid = getUserId(auth)
    // Get users private doc
    const userPrivateDoc = await getUserPrivateDoc()
    // Get user's storage IDs.
    const storageIds = userPrivateDoc.storageIds || []
    console.log("storageIds: ", storageIds);
    // If the user has no storages, return an empty array.
    if (storageIds.length === 0) return []
    // Create new storage array.
    const storages: StorageRead[] = []
    // Load each storage from the storages collection.
    await Promise.all(storageIds.map(async (storageId: string) => {
        try {
            const storageDocRef = doc(firestore, 'storages', storageId)
            const storageDocSnap = await getDoc(storageDocRef)
            const storageData = storageDocSnap.data() as any
            if (!storageData) return

            // Fast-deleted storages are hidden even when deleted storages are requested.
            if (storageData.instantDeletion === true) {
                return
            }
            
            // Filter out deleted storages if includeDeleted is false
            if (!includeDeleted && storageData.isDeleted) {
                return
            }
            
            // Get amount of documents in product collection.
            //const storageProductsCollRef = collection(storageDoc, 'products')
            //const productAmountSnapshot = await getCountFromServer(storageProductsCollRef)
            // Add the storage to the storage array.
            storages.push({
                id: storageId,
                ...storageData
            } as StorageRead)
        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : String(error)
            logger.warn('Failed to load storage document. Likely due to inconsistency in database. The process of loading other storages will continue.', { error: errorMessage, storageId: logger.sanitizeData(storageId) })
        }
    }))
    console.log("storages: ", storages);
    // Return the user's storage array.
    return storages
}

/**
 * Get storage.
 */
export const getStorageDoc = async (storageId: string): Promise<StorageRead | null> => {
    logger.api('Getting storage document', { storageId: logger.sanitizeData(storageId) })
    logger.debug('Loading storage with doc id', { storageId: logger.sanitizeData(storageId) })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    // Get storage doc ref.
    const storageDocRef = doc(collection(firestore, 'storages'), storageId)
    // Get storage doc snapshot.
    const storageDocSnap = await getDoc(storageDocRef)
    // Get storage data.
    const storageData = storageDocSnap.data() as any
    if (storageData?.instantDeletion === true) {
        return null
    }
    // Get amount of documents in product collection.
    //const storageProductsCollRef = collection(storageDocRef, 'products')
    //const productAmountSnapshot = await getCountFromServer(storageProductsCollRef)
    // Return storage data.
    return {
        id: storageId,
        ...storageData
    } as StorageRead
}

/**
 * Increment product in inventory.
 */
export const incrementProduct = async (actionData: ProductActionWrite) => {
    logger.api('Incrementing product', { 
        storageId: logger.sanitizeData(actionData.ids?.storageId), 
        productId: logger.sanitizeData(actionData.ids?.productId),
    })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()

    try {
        const createProductAction = httpsCallable(functions, 'createProductAction')
        console.log(actionData)
        const result = await createProductAction(actionData)
        return result.data
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        logger.error('Failed to increment product', { error: errorMessage, storageId: actionData.ids?.storageId, productId: actionData.ids?.productId })
        throw error instanceof Error ? error : new Error(errorMessage)
    }
}

/**
 * Decrement product in inventory.
 */
export const decrementProduct = async (actionData: ProductActionWrite) => {
    logger.api('Decrementing product', {
        storageId: logger.sanitizeData(actionData.ids?.storageId),
        productId: logger.sanitizeData(actionData.ids?.productId)
    })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()

    try {
        const createProductAction = httpsCallable(functions, 'createProductAction')
        const result = await createProductAction(actionData)
        return result.data
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        logger.error('Failed to decrement product', { error: errorMessage, storageId: actionData.ids?.storageId, productId: actionData.ids?.productId })
        throw error instanceof Error ? error : new Error(errorMessage)
    }
}

/**
 * Adjust product in inventory.
 */
export const adjustProductInventory = async (actionData: ProductActionWrite) => {
    logger.api('Adjusting product inventory', { 
        storageId: logger.sanitizeData(actionData.ids?.storageId), 
        productId: logger.sanitizeData(actionData.ids?.productId), 
    })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()

    try {
        const createProductAction = httpsCallable(functions, 'createProductAction')
        const result = await createProductAction(actionData)
        return result.data
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        logger.error('Failed to adjust product inventory', { error: errorMessage, storageId: actionData.ids?.storageId, productId: actionData.ids?.productId })
        throw error instanceof Error ? error : new Error(errorMessage)
    }
}

/**
 * Load product action docs.
 */
export const getProductActionDocs = async ({ 
    storageId, 
    productId, 
    docLimit, 
    startAfterId,
    sortBy = ActionsSortBy.CREATED_AT,
    sortDirection = SortDirection.DESCENDING
}: { 
    storageId: string, 
    productId: string, 
    docLimit: number, 
    startAfterId?: string | null | undefined,
    sortBy?: ActionsSortBy,
    sortDirection?: SortDirection
}) => {
    logger.api('Loading product action documents', { 
        storageId: logger.sanitizeData(storageId), 
        productId: logger.sanitizeData(productId), 
        docLimit, 
        startAfterId: logger.sanitizeData(startAfterId) 
    })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    // Get storage doc ref.
    const storageDocRef = doc(collection(firestore, 'storages'), storageId)
    // Target the collection "products" ref inside the storage document.
    const storageProductsCollRef = collection(storageDocRef, 'products')
    // Get product doc ref.
    const productDocRef = doc(storageProductsCollRef, productId)
    // Target the collection "actions" ref inside the product document.
    const productActionsCollRef = collection(productDocRef, 'actions')
    // Create new actions array.
    const actions: ProductActionRead[] = []
    // Initialize storage for avatar fetching
    const storage = getStorage(app)
    try {
        // Get actions with pagination, filtering out deleted ones
        logger.debug('Getting actions with pagination', { startAfterId: logger.sanitizeData(startAfterId) })
        
        // If we have a startAfterId, we need to fetch that document first to use as cursor
        let actionsQuery;
        if (startAfterId) {
            const startAfterDocRef = doc(productActionsCollRef, startAfterId);
            const startAfterDocSnap = await getDoc(startAfterDocRef);
            
            if (startAfterDocSnap.exists()) {
                actionsQuery = query(productActionsCollRef, orderBy(sortBy, sortDirection), startAfter(startAfterDocSnap), limit(docLimit));
            } else {
                // If the cursor document doesn't exist, start fresh
                actionsQuery = query(productActionsCollRef, orderBy(sortBy, sortDirection), limit(docLimit));
            }
        } else {
            // No cursor, start from beginning
            actionsQuery = query(productActionsCollRef, orderBy(sortBy, sortDirection), limit(docLimit));
        }
        const querySnapshot = await getDocs(actionsQuery)
        
        // Process each action document
        await Promise.all(querySnapshot.docs.map(async (actionDoc) => {
            let docData = actionDoc.data() as ProductActionRead
            
            actions.push({
                id: actionDoc.id,
                storageId: storageId,
                productId: productId,
                ...docData
            } as ProductActionRead)
        }))
        console.log("actions: ", actions);
        return actions
    } catch (error: unknown) {
        throw error instanceof Error ? error : new Error(String(error))
    }
}

/**
 * Get product actions by action paths (for transaction page)
 */
export const getProductactionsByPaths = async ({ 
    storageId, 
    actionPaths,
    transactionId,
}: { 
    storageId: string, 
    actionPaths: string[],
    transactionId: string,
}) => {
    logger.api('Loading product actions by paths', { 
        storageId: logger.sanitizeData(storageId), 
        actionPathsCount: actionPaths.length,
        transactionId: logger.sanitizeData(transactionId),
    })
    
    // Check if the user is logged in
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()

    // If no action paths, return empty array
    if (!actionPaths || actionPaths.length === 0) {
        return []
    }
    
    const actions: ProductActionRead[] = []
    
    try {
            // Load each referenced action document directly from the server
            // so draft/published flags are not served from the Firestore cache.
            await Promise.all(actionPaths.map(async (actionPath) => {
                try {
                    const actionRef = doc(firestore, actionPath);
                    const actionDocSnap = await getDocFromServer(actionRef)
                    if (!actionDocSnap.exists()) {
                        return
                    }

                    let docData = actionDocSnap.data() as ProductActionRead

                    // Always skip deleted actions, but INCLUDE drafts for transaction view
                    if ((docData as any).isDeleted === true) {
                        return
                    }

                    // Derive product and storage ids from the document path
                    const productDocRef = actionRef.parent?.parent
                    const storageDocRef = productDocRef?.parent?.parent
                    const productId = productDocRef?.id
                    const actionStorageId = storageDocRef?.id

                    // Ensure the action belongs to the requested storage
                    if (!productId || !actionStorageId || actionStorageId !== storageId) {
                        return
                    }

                    actions.push({
                        ...docData,
                        id: actionDocSnap.id,
                        ids: {
                            ...(docData as any).ids,
                            storageId,
                            productId,
                            actionId: actionDocSnap.id,
                        },
                    } as ProductActionRead)
                } catch (error: unknown) {
                    // If a single action fails to load, log and continue loading others
                    const errorMessage = error instanceof Error ? error.message : String(error)
                    logger.warn('Failed to load action by reference', { error: errorMessage }, 'FIREBASE_API')
                }
            }))
        console.log("actions: ", actions);
    } catch (error: unknown) {
        throw error instanceof Error ? error : new Error(String(error))
    }
    
    return actions
}

/**
 * Add access doc to storage access collection.
 */
export const addAccessInvitationDocs = async ({ storageId, email, role, message }: { storageId: string, email: string, role: AccessRole, message: string }) => {
    logger.api('Adding access document', { 
        storageId: logger.sanitizeData(storageId), 
        email: logger.sanitizeData(email), 
        role 
    })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    
    try {
        const createAccessInvitation = httpsCallable(functions, 'createAccessInvitation')
        const result = await createAccessInvitation({
            storageId,
            accessData: {
                role,
                invitation: {
                    email,
                    message
                }
            }
        })
        return result.data
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        logger.error('Failed to add access invitation document', { error: errorMessage, storageId })
        throw error instanceof Error ? error : new Error(errorMessage)
    }
}

/**
 * Load storage access invitation docs.
 * 
 * This function is used by both the storage admins but also the users
 * for checking their of access.
 */
export const getStorageAccessInvitationDocs = async ({
    storageId,
    sortBy,
    sortDirection
}: {
    storageId: string,
    sortBy: AccessInvitationsSortBy,
    sortDirection: SortDirection
}) => {
    logger.api('Loading access documents', { 
        storageId: logger.sanitizeData(storageId),
        sortBy,
        sortDirection 
    })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    // Get storage doc ref.
    const storageDocRef = doc(collection(firestore, 'storages'), storageId)
    // Target the collection "access" ref inside the storage document.
    const storageAccessCollRef = collection(storageDocRef, 'access')
    // Create new access array.
    const accessList: AccessInvitationRead[] = []
    
    try {
        // Get access docs with sorting.
        const accessQuery = query(
            storageAccessCollRef,
            orderBy(sortBy, sortDirection)
        );
        const querySnapshot = await getDocs(accessQuery)
        
        // Process each access document and load user data from userRef
        await Promise.all(querySnapshot.docs.map(async (accessDoc) => {
            const docData = accessDoc.data()
            
            let user: AccountPublicRead | undefined;
            const userId = docData.userId || docData.user?.userId;
            if (userId) {
                try {
                    user = await getUserPublicDoc(userId);
                } catch {
                    user = undefined;
                }
            } else if (docData.userRef) {
                user = await loadUserFromRef(docData.userRef);
            }
            
            accessList.push({
                ...docData,
                id: accessDoc.id,
                userId: userId || docData.userId,
                user,
            } as AccessInvitationRead)
        }))
    } catch (error: unknown) {
        throw error instanceof Error ? error : new Error(String(error))
    }
    return accessList
}

/**
 * Set Custom Inputs.
 */
export const setCustomInputs = async (customInputData: CustomInputWrite) => {
    logger.api('Setting custom inputs', { 
        storageId: logger.sanitizeData(customInputData.ids.storageId), 
        categoryId: logger.sanitizeData(customInputData.ids.categoryId) 
    })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    
    try {
        // Call the Firebase callable function
        const setCategoryCustomInputs = httpsCallable(functions, 'setCategoryCustomInputs')
        const result = await setCategoryCustomInputs(customInputData)
        return result.data
    } catch (error: unknown) {
        logger.error('Error setting custom inputs', error)
        throw error
    }
}


/**
 * Get transactions.
 */

export const getTransactionDocs = async ({
    storageId,
    startAfterData,
    docLimit,
    sortBy,
    sortDirection
}: {
    storageId: string,
    startAfterData: string | number | null | undefined,
    docLimit: number,
    sortBy: TransactionsSortBy,
    sortDirection: SortDirection
}) => {
    logger.api('Loading transactions', {
        storageId: logger.sanitizeData(storageId),
        lastTransactionId: logger.sanitizeData(startAfterData),
        docLimit
    })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    // Get storage doc ref.
    const storageDocRef = doc(collection(firestore, 'storages'), storageId)
    // Target the collection "transactions" ref inside the storage document.
    const transactionsCollRef = collection(storageDocRef, 'transactions')
    // Create new transactions object.
    let newTransactions: TransactionRead[] = []

    try {
        // Get transactions with pagination, filtering out deleted ones
        let transactionsQuery = startAfterData
            ? query(
                transactionsCollRef,
                orderBy(sortBy, sortDirection),
                startAfter(startAfterData),
                limit(docLimit)
            )
            : query(
                transactionsCollRef,
                orderBy(sortBy, sortDirection),
                limit(docLimit)
            );
        const querySnapshot = await getDocs(transactionsQuery)
        
        // Process each transaction document
        querySnapshot.docs.forEach((transactionDoc) => {
            const docData = transactionDoc.data();
            const isDraft = docData.isDraft === true;
            newTransactions.push({
                id: transactionDoc.id,
                ...docData,
            } as TransactionRead);
        });
    } catch (error: unknown) {
        throw error instanceof Error ? error : new Error(String(error))
    }
    return newTransactions
}

/**
 * Get a single transaction by doc id.
 */
export const getTransactionDoc = async ({ storageId, transactionId }: { storageId: string, transactionId: string }) => {
    logger.api('Loading transaction', {
        storageId: logger.sanitizeData(storageId),
        transactionId: logger.sanitizeData(transactionId)
    });
    if (userIdExists(auth) === false) throw new UserNonExistingIdError();
    const storageDocRef = doc(collection(firestore, 'storages'), storageId);
    const transactionsCollRef = collection(storageDocRef, 'transactions');
    const transactionDocRef = doc(transactionsCollRef, transactionId);
    const transactionDocSnap = await getDoc(transactionDocRef);
    try {
        const docData = transactionDocSnap.data();
        if (!docData) throw new Error('Transaction not found.');
        if (docData.isDeleted === true) throw new Error('Transaction has been deleted.');
        return {
            ...docData,
        } as TransactionRead;
    } catch (error: unknown) {
        throw error instanceof Error ? error : new Error(String(error));
    }
};

/**
 * Add a new transaction.
 */
export const addTransactionDoc = async (transactionData: TransactionWrite) => {
    logger.api('Adding transaction', { 
        storageId: logger.sanitizeData(transactionData.ids?.storageId),
        type: transactionData.type 
    })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    // Add transaction document.
    try {
        const createTransaction = httpsCallable(functions, 'createTransaction')
        console.log("transactionData: ", transactionData)
        const result = await createTransaction(transactionData)
        return result.data
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        logger.error('Failed to add transaction document', { error: errorMessage, storageId: transactionData.ids?.storageId })
        throw error instanceof Error ? error : new Error(errorMessage)
    }
}

/**
 * Update an existing transaction.
 */
export const updateTransactionDoc = async (updateData: TransactionWrite) => {
    logger.api('Updating transaction', { 
        storageId: logger.sanitizeData(updateData.ids?.storageId),
        transactionId: logger.sanitizeData(updateData.ids?.transactionId)
    })
    // Check if the user is logged in.
    if (userIdExists(auth) === false) throw new UserNonExistingIdError()
    // Update transaction document.
    try {
        const updateTransaction = httpsCallable(functions, 'updateTransaction')
        const result = await updateTransaction(updateData)
        return result.data
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        logger.error('Failed to update transaction document', { 
            error: errorMessage, 
            storageId: updateData.ids?.storageId,
            transactionId: updateData.ids?.transactionId 
        })
        throw error instanceof Error ? error : new Error(errorMessage)
    }
}

/**
 * Finalize (publish) a draft transaction.
 *
 * This will validate all linked draft actions on the backend, ensure
 * stock constraints are respected (no negative inventory unless
 * explicitly allowed per product), apply the corresponding stock
 * updates, and mark the transaction and actions as finalized.
 */
export const finalizeTransactionDoc = async ({
    storageId,
    transactionId,
}: {
    storageId: string;
    transactionId: string;
}) => {
    logger.api('Finalizing transaction', {
        storageId: logger.sanitizeData(storageId),
        transactionId: logger.sanitizeData(transactionId),
    });

    if (userIdExists(auth) === false) throw new UserNonExistingIdError();

    try {
        const finalizeTransaction = httpsCallable(functions, 'finalizeTransaction');
        const result = await finalizeTransaction({ storageId, transactionId });
        return result.data;
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        logger.error('Failed to finalize transaction', {
            error: errorMessage,
            storageId,
            transactionId,
        });
        throw error instanceof Error ? error : new Error(errorMessage);
    }
}

/**
 * Delete a draft product action and unlink it from its transaction.
 */
export const deleteProductActionDoc = async ({
    storageId,
    productId,
    actionId,
}: {
    storageId: string;
    productId: string;
    actionId: string;
}) => {
    logger.api('Deleting product action', {
        storageId: logger.sanitizeData(storageId),
        productId: logger.sanitizeData(productId),
        actionId: logger.sanitizeData(actionId),
    });

    if (userIdExists(auth) === false) throw new UserNonExistingIdError();

    try {
        const deleteProductAction = httpsCallable(functions, 'deleteProductAction');
        await deleteProductAction({ storageId, productId, actionId });
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        logger.error('Failed to delete product action', {
            error: errorMessage,
            storageId,
            productId,
            actionId,
        });
        throw error instanceof Error ? error : new Error(errorMessage);
    }
}

/**
 * Delete a draft transaction (hard delete).
 */
export const deleteTransactionDoc = async ({
    storageId,
    transactionId,
}: {
    storageId: string;
    transactionId: string;
}) => {
    logger.api('Deleting transaction', {
        storageId: logger.sanitizeData(storageId),
        transactionId: logger.sanitizeData(transactionId),
    });

    if (userIdExists(auth) === false) throw new UserNonExistingIdError();

    try {
        const deleteTransaction = httpsCallable(functions, 'deleteTransaction');
        await deleteTransaction({ storageId, transactionId });
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        logger.error('Failed to delete transaction', {
            error: errorMessage,
            storageId,
            transactionId,
        });
        throw error instanceof Error ? error : new Error(errorMessage);
    }
};

